import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../hooks/useAuth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase-config';
import { Upload, Send, Sparkles } from 'lucide-react';

const CustomWorkForm = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    projectName: '',
    category: '',
    description: '',
    budget: '',
    timeline: '',
    features: '',
  });
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to submit a request.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔄 Starting request submission...');
      console.log('👤 User:', user.uid, user.email);
      
      const requestData = {
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || 'Anonymous',
        projectName: formData.projectName,
        category: formData.category,
        description: formData.description,
        budget: formData.budget,
        timeline: formData.timeline,
        features: formData.features,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        project: 'transfer-e077b'
      };

      console.log('📝 Request data:', requestData);
      
      // Add to requests collection
      const docRef = await addDoc(collection(db, 'requests'), requestData);
      
      console.log('✅ Request created with ID:', docRef.id);
      
      // SUCCESS - Show prominent confirmation
      toast({
        title: '🎉 Request Submitted Successfully!',
        description: (
          <div>
            <p><strong>Project:</strong> {formData.projectName}</p>
            <p><strong>Category:</strong> {formData.category}</p>
            <p><strong>Request ID:</strong> {docRef.id}</p>
            <p style={{ marginTop: '8px', color: '#00FFD1' }}>
              ✅ Check Discord for confirmation and updates
            </p>
          </div>
        ),
        duration: 8000, // Show for 8 seconds
      });
      
      // Reset form
      setFormData({
        projectName: '',
        category: '',
        description: '',
        budget: '',
        timeline: '',
        features: '',
      });
      setFile(null);
      
    } catch (error) {
      console.error('❌ Error submitting request:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to submit your request. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check Firestore security rules.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      
      toast({
        title: '❌ Submission Failed',
        description: errorMessage,
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories = [
    'Economy',
    'Minigames',
    'Protection',
    'Admin Tools',
    'Gameplay',
    'Custom Mechanics',
    'Integration',
    'Other'
  ];

  return (
    <Card
      className="custom-work-card"
      style={{
        background: '#121212',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        borderRadius: '0px',
        padding: '60px',
      }}
    >
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Sparkles className="w-8 h-8" style={{ color: '#8B5CF6' }} />
          <h2
            className="text-4xl font-semibold"
            style={{
              color: '#FFFFFF',
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Request Custom Plugin
          </h2>
        </div>
        <p className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
          Tell us about your project and we'll build a custom solution for your server.
        </p>
        {user && (
          <p className="text-sm mt-2" style={{ color: '#00FFD1' }}>
            Submitting as: {user.displayName || user.email}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Project Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="projectName" className="text-white mb-2 block">
              Plugin Name *
            </Label>
            <Input
              id="projectName"
              name="projectName"
              value={formData.projectName}
              onChange={handleChange}
              required
              placeholder="My Custom Plugin"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '14px',
                fontSize: '16px',
              }}
            />
          </div>
          
          <div>
            <Label htmlFor="category" className="text-white mb-2 block">
              Category *
            </Label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '14px',
                fontSize: '16px',
                width: '100%',
              }}
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="description" className="text-white mb-2 block">
            Project Description *
          </Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={5}
            placeholder="Describe your plugin idea in detail..."
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              color: '#FFFFFF',
              borderRadius: '0px',
              padding: '14px',
              fontSize: '16px',
            }}
          />
        </div>

        <div>
          <Label htmlFor="features" className="text-white mb-2 block">
            Key Features
          </Label>
          <Textarea
            id="features"
            name="features"
            value={formData.features}
            onChange={handleChange}
            rows={4}
            placeholder="List the main features you need (one per line)..."
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              color: '#FFFFFF',
              borderRadius: '0px',
              padding: '14px',
              fontSize: '16px',
            }}
          />
        </div>

        {/* Budget and Timeline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="budget" className="text-white mb-2 block">
              Budget Range (USD)
            </Label>
            <Input
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              placeholder="$1 - $50"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '14px',
                fontSize: '16px',
              }}
            />
          </div>
          <div>
            <Label htmlFor="timeline" className="text-white mb-2 block">
              Timeline
            </Label>
            <Input
              id="timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              placeholder="2-3 weeks"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '14px',
                fontSize: '16px',
              }}
            />
          </div>
        </div>

        {/* File Upload */}
        <div>
          <Label htmlFor="file" className="text-white mb-2 block">
            Attachments (Optional)
          </Label>
          <div
            className="border-2 border-dashed p-6 text-center cursor-pointer hover:border-[#0EA5FF] transition-colors duration-300"
            style={{
              borderColor: 'rgba(255, 255, 255, 0.25)',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '0px',
            }}
            onClick={() => document.getElementById('file').click()}
          >
            <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: '#0EA5FF' }} />
            <p style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {file ? file.name : 'Click to upload files (docs, images, etc.)'}
            </p>
            <input id="file" type="file" className="hidden" onChange={handleFileChange} />
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting || !user}
          className="w-full"
          style={{
            background: '#00FFD1',
            color: '#000000',
            borderRadius: '0px',
            padding: '16px 24px',
            fontSize: '18px',
            fontWeight: 500,
            minHeight: '56px',
            border: 'none',
            transition: 'all 0.4s ease-in-out',
          }}
        >
          {isSubmitting ? (
            'Submitting...'
          ) : !user ? (
            'Please Sign In to Submit'
          ) : (
            <>
              Submit Request
              <Send className="w-5 h-5 ml-2 inline" />
            </>
          )}
        </Button>
      </form>
    </Card>
  );
};

export default CustomWorkForm;